import React, { useState, useEffect } from 'react';
import ReportModal from './ReportModal';
import TradeChartModal from './TradeChartModal';
import { useTranslation } from '../i18n/LanguageContext';
import { guessQuoteAsset } from '../utils/symbol';

const PAGE_SIZES = [5, 20, 50, 100, 500];

function fmt(n, d = 4) {
  if (n == null) return '—';
  return parseFloat(n).toFixed(d);
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })
       + ' '
       + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}

function pageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  if (current <= 4) {
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push('…');
    pages.push(total);
  } else if (current >= total - 3) {
    pages.push(1);
    pages.push('…');
    for (let i = total - 4; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    pages.push('…');
    for (let i = current - 1; i <= current + 1; i++) pages.push(i);
    pages.push('…');
    pages.push(total);
  }
  return pages;
}

export default function TradeHistory({ trades, onRefresh, onDelete, onWatch, onClose, watchedIds = [], isAdmin, token }) {
  const { t } = useTranslation();
  const [selected,    setSelected]    = useState(new Set());
  const [closing,     setClosing]     = useState(null);
  const [showReport,  setShowReport]  = useState(false);
  const [chartTrade, setChartTrade] = useState(null);
  const [page,        setPage]        = useState(1);
  const [pageSize,    setPageSize]    = useState(5);
  const [filterSym,   setFilterSym]   = useState('');
  const [filterSide,  setFilterSide]  = useState('');
  const [filterStatus,setFilterStatus]= useState('');

  // trades veya filtre değişince 1. sayfaya dön ve seçimi temizle
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [trades, filterSym, filterSide, filterStatus]);

  const filtered = trades.filter(t => {
    if (filterSym    && !t.symbol?.toUpperCase().includes(filterSym.toUpperCase())) return false;
    if (filterSide   && t.side   !== filterSide)   return false;
    if (filterStatus && t.status !== filterStatus) return false;
    return true;
  });

  const totalPages  = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage    = Math.min(page, totalPages);
  const pageStart   = (safePage - 1) * pageSize;
  const pagedTrades = filtered.slice(pageStart, pageStart + pageSize);

  const toggleRow = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const pageIds    = pagedTrades.map(t => t.id);
  const allChecked = pageIds.length > 0 && pageIds.every(id => selected.has(id));

  const toggleAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allChecked) pageIds.forEach(id => next.delete(id));
      else            pageIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleDelete = () => {
    const ids = selected.size > 0 ? [...selected] : null;
    const label = ids ? t('tradeHistory.deleteConfirmSelected', { n: ids.length }) : t('tradeHistory.deleteConfirmAll');
    if (!window.confirm(t('tradeHistory.deleteConfirm', { label }))) return;
    onDelete?.(ids);
    setSelected(new Set());
  };

  const goTo = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

  const someChecked = selected.size > 0;

  return (
    <div>
      {showReport && <ReportModal token={token} onClose={() => setShowReport(false)} />}
      {chartTrade && <TradeChartModal trade={chartTrade} token={token} onClose={() => setChartTrade(null)} />}
      <div className="history-header">
        <p className="card-title" style={{ marginBottom: 0 }}>{t('tradeHistory.title')}</p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button className="btn-report" onClick={() => setShowReport(true)}>{t('tradeHistory.report')}</button>
          {isAdmin && someChecked && (
            <button className="btn-refresh" style={{ color: 'var(--danger)' }} onClick={handleDelete}>
              {t('tradeHistory.deleteSelected', { n: selected.size })}
            </button>
          )}
          {isAdmin && !someChecked && trades.length > 0 && (
            <button className="btn-refresh" style={{ color: 'var(--danger)' }} onClick={handleDelete}>
              {t('tradeHistory.deleteAll')}
            </button>
          )}
          <button className="btn-refresh" onClick={onRefresh}>{t('tradeHistory.refresh')}</button>
        </div>
      </div>

      {/* Filtre satırı */}
      <div className="trade-filter-bar">
        <input
          className="trade-filter-input"
          placeholder={t('tradeHistory.searchPlaceholder')}
          value={filterSym}
          onChange={e => setFilterSym(e.target.value)}
        />
        <select className="trade-filter-select" value={filterSide} onChange={e => setFilterSide(e.target.value)}>
          <option value="">{t('tradeHistory.allSides')}</option>
          <option value="long">Long</option>
          <option value="short">Short</option>
        </select>
        <select className="trade-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">{t('tradeHistory.allStatuses')}</option>
          <option value="open">Open</option>
          <option value="closed">Closed</option>
        </select>
        {(filterSym || filterSide || filterStatus) && (
          <button className="trade-filter-clear" onClick={() => { setFilterSym(''); setFilterSide(''); setFilterStatus(''); }}>
            {t('tradeHistory.clearFilters')}
          </button>
        )}
      </div>

      {trades.length === 0 ? (
        <div className="empty-state">{t('tradeHistory.noTrades')}</div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">{t('tradeHistory.noFilterMatch')}</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className="trade-table">
              <thead>
                <tr>
                  {isAdmin && (
                    <th style={{ width: 32, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={toggleAll}
                        style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
                      />
                    </th>
                  )}
                  <th>{t('tradeHistory.colSymbol')}</th>
                  <th>{t('tradeHistory.colSide')}</th>
                  <th>{t('tradeHistory.colEntry')}</th>
                  <th>{t('tradeHistory.colExit')}</th>
                  <th>{t('tradeHistory.colSl')}</th>
                  <th>{t('tradeHistory.colTp')}</th>
                  <th>P&amp;L</th>
                  <th>Fee</th>
                  <th>{t('tradeHistory.colMargin')}</th>
                  <th>{t('tradeHistory.colLeverage')}</th>
                  <th title={t('tradeHistory.colAnalysisPeriodTitle')}>{t('tradeHistory.colAnalysisPeriod')}</th>
                  <th>{t('tradeHistory.colSignal')}</th>
                  <th>{t('tradeHistory.colStatus')}</th>
                  <th title={t('tradeHistory.colOpenCloseTimeTitle')}>{t('tradeHistory.colOpenCloseTime')}</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {pagedTrades.map(trade => {
                  const pnl        = trade.pnl != null ? parseFloat(trade.pnl) : null;
                  const isSelected = selected.has(trade.id);
                  const isWatched  = watchedIds.includes(trade.id);
                  const isOpen     = trade.status === 'open';

                  return (
                    <tr
                      key={trade.id}
                      style={{
                        background: isWatched ? 'var(--accent)12' : isSelected ? 'var(--accent)18' : undefined,
                        cursor: (isAdmin || isOpen) ? 'pointer' : 'default',
                      }}
                      onClick={() => {
                        if (isOpen && onWatch) { onWatch(trade); return; }
                        if (isAdmin) toggleRow(trade.id);
                      }}
                      title={isOpen ? (isWatched ? t('tradeHistory.watching') : t('tradeHistory.clickToWatch')) : undefined}
                    >
                      {isAdmin && (
                        <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleRow(trade.id)}
                            style={{ cursor: 'pointer', accentColor: 'var(--accent)' }}
                          />
                        </td>
                      )}
                      <td style={{ fontWeight: 700 }}>
                        {trade.symbol}
                        {trade.market === 'spot' && (
                          <span style={{
                            marginLeft: 5, fontSize: 9, fontWeight: 700,
                            background: 'rgba(13,148,136,0.12)', color: 'var(--accent)',
                            border: '1px solid rgba(13,148,136,0.4)',
                            borderRadius: 3, padding: '1px 4px', verticalAlign: 'middle',
                          }}>SPOT</span>
                        )}
                        {trade.is_test && (
                          <span style={{
                            marginLeft: 5, fontSize: 9, fontWeight: 700,
                            background: 'rgba(161,97,210,0.15)', color: '#a161d2',
                            border: '1px solid rgba(161,97,210,0.4)',
                            borderRadius: 3, padding: '1px 4px', verticalAlign: 'middle',
                          }}>TEST</span>
                        )}
                      </td>
                      <td>
                        <span className={`position-side side-${trade.side}`}
                              style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, display: 'inline-block' }}>
                          {trade.market === 'spot' ? t('tradeHistory.buyLabel') : trade.side?.toUpperCase()}
                        </span>
                      </td>
                      <td>{fmt(trade.entry_price)}</td>
                      <td>{fmt(trade.exit_price)}</td>
                      <td style={{ color: 'var(--red)' }}>{fmt(trade.sl_price)}</td>
                      <td style={{ color: 'var(--accent)' }}>{fmt(trade.tp_price)}</td>
                      <td className={pnl == null ? '' : pnl >= 0 ? 'pnl-pos' : 'pnl-neg'}>
                        {pnl == null ? '—' : `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}`}
                      </td>
                      <td style={{ color: 'var(--muted)', fontSize: 12 }}>
                        {trade.fee != null ? parseFloat(trade.fee).toFixed(4) : '—'}
                      </td>
                      <td style={{ color: 'var(--muted)' }}>
                        {trade.market === 'spot'
                          ? (trade.quantity != null ? `${parseFloat(trade.quantity)} ${guessQuoteAsset(trade.symbol)}` : '—')
                          : (trade.margin != null ? `${parseFloat(trade.margin)} $` : '—')}
                      </td>
                      <td>{trade.market === 'spot' ? '—' : `${trade.leverage}x`}</td>
                      <td style={{ color: 'var(--accent)', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 700 }}>
                        {trade.analysis_interval || '—'}
                      </td>
                      <td style={{ color: 'var(--muted)', textTransform: 'capitalize' }}>{trade.signal_type}</td>
                      <td>
                        <span className={`status-chip status-${trade.status}`}>
                          {isWatched ? '● ' : ''}{trade.status}
                        </span>
                      </td>
                      <td style={{ color: 'var(--muted)' }}>
                        <div>{fmtDate(trade.created_at)}</div>
                        <div style={{ fontSize: 10, opacity: 0.7 }}>{trade.closed_at ? fmtDate(trade.closed_at) : '—'}</div>
                      </td>
                      <td style={{ display: 'flex', gap: 4 }} onClick={e => e.stopPropagation()}>
                        {isAdmin && (
                          <button
                            className="btn-refresh"
                            title={t('tradeHistory.visualizeTitle')}
                            onClick={() => setChartTrade({
                              symbol: trade.symbol, interval: trade.analysis_interval || '5m', strategy: trade.signal_type,
                              market: trade.market,
                              side: trade.side, entryTs: new Date(trade.created_at).getTime(),
                              exitTs: trade.closed_at ? new Date(trade.closed_at).getTime() : null,
                              entryPrice: parseFloat(trade.entry_price), exitPrice: trade.exit_price != null ? parseFloat(trade.exit_price) : null,
                              slPrice: trade.sl_price != null ? parseFloat(trade.sl_price) : null,
                              tpPrice: trade.tp_price != null ? parseFloat(trade.tp_price) : null,
                              pnl: trade.pnl,
                              exitReason: trade.exit_reason ?? null, config: trade.config ?? null,
                            })}
                          >
                            📈
                          </button>
                        )}
                        {isAdmin && isOpen && (
                          <button
                            className="btn-close-trade"
                            disabled={closing === trade.id}
                            onClick={async (e) => {
                              e.stopPropagation();
                              setClosing(trade.id);
                              try { await onClose?.(trade.id); } finally { setClosing(null); }
                            }}
                          >
                            {closing === trade.id ? '…' : '✕'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-bar">
            <div className="pagination-left">
              <span className="pagination-info">
                {pageStart + 1}–{Math.min(pageStart + pageSize, filtered.length)} / {filtered.length}
                {filtered.length !== trades.length && ` ${t('tradeHistory.total', { n: trades.length })}`}
              </span>
              <select
                className="pagination-size"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
              >
                {PAGE_SIZES.map(s => (
                  <option key={s} value={s}>{t('tradeHistory.rowsPerPage', { n: s })}</option>
                ))}
              </select>
            </div>

            <div className="pagination-controls">
              <button className="pg-btn" onClick={() => goTo(1)}          disabled={safePage === 1}>«</button>
              <button className="pg-btn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1}>‹</button>

              {pageNumbers(safePage, totalPages).map((p, i) =>
                p === '…'
                  ? <span key={`e${i}`} className="pg-ellipsis">…</span>
                  : <button
                      key={p}
                      className={`pg-btn${p === safePage ? ' pg-btn-active' : ''}`}
                      onClick={() => goTo(p)}
                    >{p}</button>
              )}

              <button className="pg-btn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages}>›</button>
              <button className="pg-btn" onClick={() => goTo(totalPages)}   disabled={safePage === totalPages}>»</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
